import { Lock, Zap } from "lucide-react"
import { useEffect } from "react"
import { Link } from "react-router-dom"
import { AppHeader } from "@/components/dashboard/AppHeader"
import { EarlyMomentumProgressBar } from "@/components/dashboard/EarlyMomentumProgressBar"
import { useDashboardGuard } from "@/lib/activation"
import { useDealPipeline } from "@/lib/deal-pipeline-context"
import { cn } from "@/lib/utils"

const TONE_STYLES = {
  primary: {
    container: "border-primary/20 bg-primary/5",
    stroke: "text-primary",
    accent: "text-primary",
    micro: "text-primary/70",
  },
  warning: {
    container: "border-chart-4/20 bg-chart-4/5",
    stroke: "text-chart-4",
    accent: "text-chart-4",
    micro: "text-chart-4/70",
  },
  muted: {
    container: "border-border",
    stroke: "text-muted-foreground",
    accent: "text-muted-foreground",
    micro: "text-muted-foreground/70",
  },
}

const PIPELINE_TARGET = 20
const PIPELINE_VELOCITY_THRESHOLD = 10
const APPRAISAL_STALL_DAYS = 7
const FOLLOW_UP_STALL_DAYS = 3
const ANALYSED_STAGES = ["Appraising", "Offer Ready", "In Negotiation", "Follow Up", "Legal / Due Diligence", "Completed"]
const STAGE_PRIORITY = {
  "Offer Ready": 1,
  "In Negotiation": 2,
  Appraising: 3,
  Identified: 4,
  "Follow Up": 5,
  "Legal / Due Diligence": 6,
  Completed: 7,
}

function activeDeals(deals) {
  return deals.filter((deal) => deal.outcome === "Open")
}

function priorityDealScoreLabel(deal) {
  return Number.isFinite(deal.score) ? deal.score : "Building"
}

function pipelineVelocityMetric(summary, activePipelineCount) {
  const velocity = summary.pipelineVelocity
  const threshold = velocity?.activationThreshold ?? PIPELINE_VELOCITY_THRESHOLD
  const isActive = velocity?.status === "active" && activePipelineCount >= threshold

  if (!isActive || !Number.isFinite(velocity?.score)) {
    return {
      title: "Pipeline Velocity",
      display: "Building",
      subtext: `${activePipelineCount}/${threshold} Active Deals`,
      percentage: Math.min(100, (activePipelineCount / threshold) * 100),
      tone: "muted",
      microcopy: `Activates at ${threshold} active deals`,
    }
  }

  return {
    title: "Pipeline Velocity",
    display: `${velocity.score}%`,
    subtext: velocity.label ?? "Velocity Score",
    percentage: velocity.score,
    tone: velocity.score >= 70 ? "primary" : "warning",
    microcopy: `${velocity.activeDealsCount ?? activePipelineCount} active deals · input ${velocity.components?.pipelineInputRate ?? 0}/20`,
  }
}

function dashboardMetrics(summary, deals) {
  const activePipelineCount = summary.activeDealsCount ?? activeDeals(deals).length

  return [
    pipelineVelocityMetric(summary, activePipelineCount),
    {
      title: "Execution Score",
      display: "Coming Soon",
      subtext: "Execution Discipline",
      percentage: 0,
      tone: "muted",
      microcopy: "Action follow-through scoring is on the roadmap",
    },
    {
      title: "Wealth Trajectory",
      display: "Coming Soon",
      subtext: "Wealth Outlook",
      percentage: 0,
      tone: "muted",
      microcopy: "Portfolio trajectory modelling is on the roadmap",
    },
  ]
}

function actionPathForDeal(deal) {
  if (deal.stage === "Appraising") return `/deal-pipeline/${deal.id}?tab=appraisal`
  if (["Offer Ready", "In Negotiation", "Follow Up", "Legal / Due Diligence", "Completed"].includes(deal.stage)) {
    return `/deal-pipeline/${deal.id}?tab=negotiation`
  }

  return `/deal-pipeline/${deal.id}`
}

function priorityTaskFromSummary(priority) {
  return {
    id: priority.id,
    action: priority.action,
    ref: priority.dealName,
    stage: priority.currentStage,
    reason: priority.reason,
    path: priority.path,
    cta: priority.cta ?? "Open Deal",
  }
}

function priorityWeight(deal) {
  const stageWeight = STAGE_PRIORITY[deal.stage] ?? 99
  const inactivityWeight = Number.isInteger(deal.inactivityDays) ? deal.inactivityDays : 0
  const scoreWeight = Number.isFinite(deal.score) ? deal.score / 100 : 0

  return stageWeight - inactivityWeight / 100 - scoreWeight / 1000
}

function priorityDeals(deals) {
  return [...activeDeals(deals)]
    .sort((left, right) => priorityWeight(left) - priorityWeight(right))
    .slice(0, 3)
}

function pluralizeDeal(count) {
  return count === 1 ? "deal" : "deals"
}

function numberLabel(count) {
  const labels = {
    1: "One",
    2: "Two",
    3: "Three",
    4: "Four",
    5: "Five",
    6: "Six",
    7: "Seven",
    8: "Eight",
    9: "Nine",
    10: "Ten",
  }

  return labels[count] ?? String(count)
}

function maxInactivityDays(deals) {
  return deals.reduce((maxDays, deal) => (
    Number.isInteger(deal.inactivityDays) ? Math.max(maxDays, deal.inactivityDays) : maxDays
  ), 0)
}

function behavioralInsightCandidates(activePipelineCount, summary, deals) {
  const activePipelineDeals = activeDeals(deals)
  const pipelineThreshold = summary.pipelineVelocity?.activationThreshold ?? PIPELINE_VELOCITY_THRESHOLD
  const [topPriority] = summary.dailyPriorities ?? []
  const analysedDeals = activePipelineDeals.filter((deal) => (
    ANALYSED_STAGES.includes(deal.stage) || (deal.detailUnlockStage ?? 0) >= 4
  ))
  const stalledAppraisals = activePipelineDeals.filter((deal) => (
    deal.stage === "Appraising" && Number.isInteger(deal.inactivityDays) && deal.inactivityDays >= APPRAISAL_STALL_DAYS
  ))
  const offerReadyDeals = activePipelineDeals.filter((deal) => deal.stage === "Offer Ready")
  const staleFollowUps = activePipelineDeals.filter((deal) => (
    ["In Negotiation", "Follow Up"].includes(deal.stage) &&
    Number.isInteger(deal.inactivityDays) &&
    deal.inactivityDays >= FOLLOW_UP_STALL_DAYS
  ))
  const candidates = []

  if (activePipelineCount < pipelineThreshold) {
    const gap = pipelineThreshold - activePipelineCount
    candidates.push({
      score: 90 + gap,
      title: "Pipeline depth is below target.",
      message: `You currently have ${activePipelineCount} active opportunities; based on your acquisition goal, you need ${pipelineThreshold}+ to maintain offer velocity.`,
    })
  }

  if (stalledAppraisals.length > 0) {
    candidates.push({
      score: 95 + stalledAppraisals.length * 4 + maxInactivityDays(stalledAppraisals),
      title: `${numberLabel(stalledAppraisals.length)} ${pluralizeDeal(stalledAppraisals.length)} ${stalledAppraisals.length === 1 ? "has" : "have"} been stuck in appraisal for over ${APPRAISAL_STALL_DAYS} days.`,
      message: "Your current constraint is appraisal completion, not sourcing.",
    })
  }

  if (analysedDeals.length > 0 && (summary.offersSubmittedCount ?? 0) === 0) {
    candidates.push({
      score: 94 + analysedDeals.length,
      title: `You have analysed ${analysedDeals.length} ${pluralizeDeal(analysedDeals.length)} but made no offers.`,
      message: "Sentinel recommends moving one opportunity to offer-ready within 48 hours.",
    })
  }

  if (offerReadyDeals.length > 0) {
    candidates.push({
      score: 88 + offerReadyDeals.length * 3,
      title: `${offerReadyDeals.length} ${pluralizeDeal(offerReadyDeals.length)} ${offerReadyDeals.length === 1 ? "is" : "are"} ready for offer action.`,
      message: "Your current behavioural constraint is converting completed appraisal work into offers.",
    })
  }

  if (staleFollowUps.length > 0) {
    candidates.push({
      score: 86 + staleFollowUps.length * 3 + maxInactivityDays(staleFollowUps),
      title: `${staleFollowUps.length} active ${pluralizeDeal(staleFollowUps.length)} need follow-up discipline.`,
      message: "Your current behavioural constraint is keeping negotiation and follow-up momentum moving.",
    })
  }

  if (topPriority) {
    candidates.push({
      score: topPriority.sentinelDailyPriorityScore ?? topPriority.dealActionPriorityScore ?? 75,
      title: `Current execution constraint: ${topPriority.reason}.`,
      message: `${topPriority.action} on ${topPriority.dealName}.`,
    })
  }

  return candidates
}

function matureSystemInsight(activePipelineCount, summary, deals) {
  const [topCandidate] = behavioralInsightCandidates(activePipelineCount, summary, deals)
    .sort((left, right) => right.score - left.score)

  if (topCandidate) return topCandidate

  if (activePipelineCount >= PIPELINE_TARGET) {
    return {
      title: "Your active deal pipeline has reached the minimum depth target.",
      message: "Keep progressing appraisals and offers so pipeline depth converts into outcomes.",
    }
  }

  return {
    title: "System insight is building.",
    message: "Sentinel will surface diagnostic guidance once enough persisted activity exists.",
  }
}

function systemInsightForPipeline(activePipelineCount, summary, deals) {
  if (summary.latestUnreadListingChange) {
    return {
      title: summary.latestUnreadListingChange.title,
      message: summary.latestUnreadListingChange.message,
      ctaLabel: "View change",
      ctaPath: summary.latestUnreadListingChange.path,
    }
  }

  if (activePipelineCount === 0) {
    return {
      title: "Welcome to Sentinel",
      message: "Welcome to Sentinel. Everything starts with your pipeline. Add your first deal and Sentinel will begin learning how you invest, identify opportunities, and help prioritise your next actions.",
      ctaLabel: "Add your first deal",
      ctaPath: "/deal-pipeline",
    }
  }

  if (activePipelineCount <= 2) {
    return {
      title: "Good start.",
      message: "Continue adding live opportunities so Sentinel can begin identifying patterns and build a complete picture of your acquisition pipeline.",
    }
  }

  if (activePipelineCount <= 4) {
    return {
      title: "Now it can begin coaching.",
      message: "Your pipeline is still relatively small. Maintaining a healthy flow of opportunities gives Sentinel more options to prioritise and improves decision quality.",
    }
  }

  return matureSystemInsight(activePipelineCount, summary, deals)
}

export function DashboardScreen() {
  const guard = useDashboardGuard()
  const {
    deals,
    error,
    isLoading,
    refreshDeals,
    summary,
  } = useDealPipeline()
  const activePipelineCount = summary.activeDealsCount ?? 0
  const systemInsight = systemInsightForPipeline(activePipelineCount, summary, deals)
  const metrics = dashboardMetrics(summary, deals)
  const topDeals = priorityDeals(deals)
  const persistedPriorities = summary.dailyPriorities ?? []
  const priorities = persistedPriorities.map(priorityTaskFromSummary)

  useEffect(() => {
    if (guard) return undefined

    let active = true
    refreshDeals?.().catch(() => {
      if (!active) return
    })

    return () => { active = false }
  }, [guard, refreshDeals])

  if (guard) return guard

  return (
    <main className="min-h-screen bg-background text-foreground">
      <AppHeader active="dashboard" status="online" showNav />

      <EarlyMomentumProgressBar activePipelineCount={activePipelineCount} />

      <section className="mx-auto w-full max-w-[1600px] p-4 md:p-6 lg:p-8">
        <div className="rounded-2xl border border-border bg-card p-4 shadow-sm sm:p-6 lg:p-8">
          <div className="mb-8">
            <div>
              <h2 className="text-2xl font-bold tracking-tight text-foreground">Execution Dashboard</h2>
              <p className="mt-1 text-sm text-muted-foreground">Real-time telemetry and deal aggregation</p>
            </div>
          </div>

          <div className="mb-6">
            <p className="inline-block rounded border border-border/50 bg-muted/50 px-3 py-1.5 text-xs font-medium text-muted-foreground">
              Prioritisation driven by deal stage, inactivity, and opportunity score
            </p>
          </div>

          <div className="flex flex-col space-y-10">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {metrics.map((m) => <MetricCard key={m.title} metric={m} />)}
            </div>

            <div className="space-y-6">
              <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
                <div className="mb-4 flex items-center gap-2">
                  <Zap className="h-4 w-4 text-primary" />
                  <h3 className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                    System Insight
                  </h3>
                </div>
                <div className="space-y-4">
                  <p className="text-base font-bold leading-snug text-foreground">
                    {systemInsight.title}
                  </p>
                  <p className="text-sm font-medium text-muted-foreground">
                    {systemInsight.message}
                  </p>
                  {systemInsight.ctaLabel ? (
                    <Link
                      to={systemInsight.ctaPath}
                      className="inline-flex items-center justify-center rounded bg-primary px-3 py-1.5 text-xs font-bold uppercase tracking-widest text-primary-foreground transition-colors hover:bg-primary/90"
                    >
                      {systemInsight.ctaLabel}
                    </Link>
                  ) : null}
                  <p className="border-t border-border pt-2 text-xs text-muted-foreground/60">
                    Based on current persisted deal pipeline state
                  </p>
                </div>
              </div>

              <div>
                <div className="mb-4">
                  <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-foreground">
                    Today&apos;s Priorities
                  </h3>
                  <p className="mt-1 text-xs text-muted-foreground">Execution tasks driven by engine logic</p>
                </div>
                <div className="flex flex-col space-y-3">
                  {isLoading ? (
                    <DashboardEmptyState title="Loading deal priorities" body="Fetching current pipeline state." />
                  ) : error ? (
                    <DashboardEmptyState title="Dashboard data unavailable" body={error} />
                  ) : priorities.length > 0 ? (
                    priorities.map((task, i) => (
                      <div
                        key={task.id}
                        className="flex flex-col justify-between gap-4 rounded-xl border border-border bg-background p-4 shadow-sm hover:border-primary/50 sm:flex-row sm:items-center"
                      >
                        <div className="flex items-start gap-4 sm:items-center">
                          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-primary bg-primary/10 text-[11px] font-bold text-primary">
                            {i + 1}
                          </div>
                          <div className="min-w-0 space-y-1">
                            <p className="text-base font-bold text-foreground">{task.action}</p>
                            <p className="text-sm font-medium text-foreground">{task.ref}</p>
                            <p className="text-xs text-muted-foreground">{task.reason}</p>
                          </div>
                        </div>
                        <div className="flex shrink-0 flex-wrap gap-2">
                          <Link
                            to={task.path}
                            aria-label={`${task.cta} for ${task.ref}`}
                            className="rounded bg-primary px-3 py-1.5 text-center text-xs font-bold uppercase tracking-widest text-primary-foreground transition-colors hover:bg-primary/90"
                          >
                            {task.cta}
                          </Link>
                        </div>
                      </div>
                    ))
                  ) : (
                    <DashboardEmptyState title="No active priorities" body="Add or progress deals to populate today's priorities." />
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 items-stretch gap-8 lg:grid-cols-2">
              <div className="flex h-full flex-col space-y-4">
                <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-muted-foreground">
                  Active Deals
                </h3>
                <div className="flex flex-1 flex-col space-y-4">
                  {topDeals.length > 0 ? (
                    topDeals.map((deal) => (
                      <div
                        key={deal.id}
                        className="flex flex-1 gap-4 rounded-xl border border-border bg-background p-4 shadow-sm hover:border-primary/50"
                      >
                        <div className="relative h-full min-h-[6rem] w-24 shrink-0 overflow-hidden rounded-lg border border-border/50 bg-muted">
                          <img
                            src={deal.image}
                            alt={deal.address}
                            loading="lazy"
                            className="absolute inset-0 h-full w-full object-cover opacity-90"
                          />
                        </div>
                        <div className="flex min-w-0 flex-1 flex-col">
                          <div className="mb-3 flex items-start justify-between gap-2">
                            <div className="min-w-0 space-y-1">
                              <p className="truncate text-sm font-bold text-foreground">{deal.address}</p>
                              <div className="text-xs text-muted-foreground">
                                <span className="font-semibold">Stage:</span> {deal.stage}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                <span className="font-semibold">Status:</span>{" "}
                                <span className="font-medium text-primary">{deal.status}</span>
                              </div>
                            </div>
                            <div className="flex shrink-0 flex-col items-end">
                              <span className="mb-0.5 text-[10px] uppercase tracking-widest text-muted-foreground">
                                Score
                              </span>
                              <span className="font-mono text-sm font-bold text-primary">{priorityDealScoreLabel(deal)}</span>
                            </div>
                          </div>
                          <div className="mt-auto border-t border-border/50 pt-2">
                            <Link
                              to={actionPathForDeal(deal)}
                              aria-label={`Open deal ${deal.address}`}
                              className="inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-primary/20 bg-primary/10 px-3 py-1.5 font-bold text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
                            >
                              <span className="text-xs font-bold uppercase tracking-widest">Open Deal</span>
                            </Link>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <DashboardEmptyState title="No active deals" body="Current-user deals will appear here once active pipeline data exists." />
                  )}
                </div>
              </div>

              <div className="flex h-full flex-col space-y-4">
                <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-muted-foreground">
                  Investor Pipeline
                  <Lock className="h-3 w-3" />
                </h3>
                <div className="relative flex flex-1 flex-col items-center justify-center overflow-hidden rounded-xl border border-border bg-background opacity-40">
                  <div className="z-10 flex flex-col items-center justify-center py-16">
                    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-border bg-muted shadow-sm">
                      <Lock className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <span className="max-w-[280px] px-4 text-center text-sm font-bold leading-tight text-foreground">
                      Complete Early Stage Momentum to unlock investor tracking
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  )
}

function DashboardEmptyState({ title, body }) {
  return (
    <div className="rounded-xl border border-dashed border-border bg-background p-6 text-center">
      <p className="text-sm font-bold text-foreground">{title}</p>
      <p className="mt-1 text-xs font-medium text-muted-foreground">{body}</p>
    </div>
  )
}

function MetricCard({ metric }) {
  const tone = TONE_STYLES[metric.tone] ?? TONE_STYLES.muted
  const displayText = String(metric.display)
  const isNumericDisplay = /^\d+%?$/.test(displayText)
  const progressPercentage = Math.max(0, Math.min(100, metric.percentage ?? 0))
  const hasProgress = progressPercentage > 0

  return (
    <div className={cn("relative flex flex-col items-center rounded-xl border bg-background p-5 text-center shadow-sm", tone.container)}>
      <p className="mb-4 text-sm font-medium text-muted-foreground">{metric.title}</p>
      <div className="relative mb-3 h-24 w-24">
        <svg className="h-full w-full -rotate-90" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" strokeWidth="6" className="text-muted opacity-50" />
          {hasProgress ? (
            <circle
              cx="50"
              cy="50"
              r="40"
              fill="none"
              stroke="currentColor"
              strokeWidth="6"
              strokeDasharray="251.2"
              strokeDashoffset={251.2 - (251.2 * progressPercentage) / 100}
              strokeLinecap="round"
              className={tone.stroke}
            />
          ) : null}
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span
            className={cn(
              "max-w-16 text-center font-mono font-bold leading-none text-foreground",
              isNumericDisplay ? "text-sm" : "text-xs",
            )}
          >
            {metric.display}
          </span>
        </div>
      </div>
      <p className={cn("text-xs font-bold uppercase tracking-widest", tone.accent)}>{metric.subtext}</p>
      {metric.microcopy ? (
        <p className={cn("mt-2 text-[10px] font-medium", tone.micro)}>{metric.microcopy}</p>
      ) : null}
    </div>
  )
}
